/**
 * Sales KPI Tracking Component
 * Shows detailed performance metrics for each Sales Rep
 */
export function SalesKPIs({ repPerformance }) {
  const formatTime = (hours) => {
    if (hours < 24) return `${hours} ساعة`;
    return `${Math.round(hours / 24)} يوم`;
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString('ar-SA')} درهم`;
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return 'لا يوجد';
    const hours = Math.floor((Date.now() - timestamp) / (1000 * 60 * 60));
    if (hours < 1) return 'الآن';
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${Math.floor(hours / 24)} يوم`;
  };

  return (
    <div style={{ animation: 'sIn 0.3s ease' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20 }}>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>إجمالي المطالبات</div>
          <div style={{ color: '#3b82f6', fontSize: 32, fontWeight: 900 }}>
            {repPerformance.reduce((sum, r) => sum + r.totalClaims, 0)}
          </div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20 }}>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>متوسط التحويل</div>
          <div style={{ color: '#10b981', fontSize: 32, fontWeight: 900 }}>
            {repPerformance.length > 0 
              ? Math.round(repPerformance.reduce((sum, r) => sum + r.conversionRate, 0) / repPerformance.length)
              : 0}%
          </div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20 }}>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>متوسط وقت الإغلاق</div>
          <div style={{ color: '#f59e0b', fontSize: 32, fontWeight: 900 }}>
            {formatTime(repPerformance.length > 0 
              ? repPerformance.reduce((sum, r) => sum + r.avgTimeToClose, 0) / repPerformance.length
              : 0)}
          </div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20 }}>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>إجمالي الإيرادات</div>
          <div style={{ color: '#10b981', fontSize: 32, fontWeight: 900 }}>
            {formatCurrency(repPerformance.reduce((sum, r) => sum + r.totalRevenue, 0))}
          </div>
        </div>
      </div>

      {/* Detailed Rep Table */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <h2 style={{ color: '#000000', fontSize: 20, fontWeight: 700, margin: 0 }}>
            📊 تفاصيل أداء المندوبين
          </h2>
        </div>

        {/* Desktop Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#ffffff' }}>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: '#000000', fontSize: 13, fontWeight: 700 }}>المندوب</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>المطالبات النشطة</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>تم الانضمام</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>معدل التحويل</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>متوسط الوقت</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>الإيرادات</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 700 }}>آخر تفاعل</th>
              </tr>
            </thead>
            <tbody>
              {repPerformance.map((rep, idx) => (
                <tr key={rep.id} style={{ 
                  background: idx % 2 === 0 ? 'transparent' : '#ffffff44',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ color: '#000000', fontSize: 15, fontWeight: 700 }}>{rep.name}</div>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: '#3b82f622',
                      color: '#60a5fa',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 700,
                    }}>
                      {rep.activeClaims}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: '#10b98122',
                      color: '#10b981',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 700,
                    }}>
                      {rep.onboarded}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: rep.conversionRate >= 50 ? '#10b98122' : rep.conversionRate >= 30 ? '#f59e0b22' : '#ef444422',
                      color: rep.conversionRate >= 50 ? '#10b981' : rep.conversionRate >= 30 ? '#f59e0b' : '#ef4444',
                      borderRadius: 12,
                      fontSize: 14,
                      fontWeight: 700,
                    }}>
                      {rep.conversionRate}%
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: '#000000', fontSize: 14 }}>
                    {rep.avgTimeToClose > 0 ? formatTime(rep.avgTimeToClose) : '-'}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: '#10b981', fontSize: 14, fontWeight: 700 }}>
                    {formatCurrency(rep.totalRevenue)}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                    {timeAgo(rep.lastInteraction)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div style={{ display: 'none', padding: 16 }}>
          <style>{`
            @media (max-width: 768px) {
              table { display: none !important; }
              div[style*="display: none"] { display: block !important; }
            }
          `}</style>
          {repPerformance.map(rep => (
            <div key={rep.id} style={{
              padding: 16,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              marginBottom: 12,
            }}>
              <div style={{ color: '#000000', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                {rep.name}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                <div>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>المطالبات النشطة</div>
                  <div style={{ color: '#3b82f6', fontSize: 18, fontWeight: 700 }}>{rep.activeClaims}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>تم الانضمام</div>
                  <div style={{ color: '#10b981', fontSize: 18, fontWeight: 700 }}>{rep.onboarded}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>معدل التحويل</div>
                  <div style={{ color: '#f59e0b', fontSize: 18, fontWeight: 700 }}>{rep.conversionRate}%</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: 4 }}>الإيرادات</div>
                  <div style={{ color: '#10b981', fontSize: 18, fontWeight: 700 }}>{formatCurrency(rep.totalRevenue)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
